import { supabase } from './supabase';

// Define available models
export const OPENAI_MODELS = {
  'gpt-4o': 'GPT-4o',
  'gpt-4o-mini': 'GPT-4o Mini',
  'gpt-4-turbo': 'GPT-4 Turbo',
  'gpt-4': 'GPT-4',
  'gpt-3.5-turbo': 'GPT-3.5 Turbo',
  'o1': 'O1',
  'o1-mini': 'O1 Mini',
  'o3-mini': 'O3 Mini',
  'o3-mini-high': 'O3 Mini High',
};

export const ANTHROPIC_MODELS = {
  'claude-3-5-sonnet-20241022': 'Claude 3.5 Sonnet',
  'claude-3-opus-20250219': 'Claude 3 Opus',
  'claude-3-sonnet-20240229': 'Claude 3 Sonnet',
  'claude-3-haiku-20240307': 'Claude 3 Haiku',
};

export const GEMINI_MODELS = {
  'gemini-2.0-flash': 'Gemini 2.0 Flash',
  'gemini-1.5-pro': 'Gemini 1.5 Pro',
  'gemini-1.5-flash': 'Gemini 1.5 Flash',
  'gemini-pro': 'Gemini Pro',
};

export const generateTaskDescription = async (
  prompt: string, 
  type: 'task' | 'research' = 'task',
  outputStyle: 'brief' | 'detailed' | 'bullet' = 'detailed',
  provider: string = 'openai',
  model: string = 'auto',
  file?: File | null
): Promise<string> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('llm_key, anthropic_key, gemini_key')
      .eq('user_id', session.user.id)
      .single();

    if (error) throw error;
    
    // Check for appropriate API key based on provider
    if (provider === 'openai' && !data?.llm_key) {
      throw new Error('No OpenAI API key found');
    } else if (provider === 'anthropic' && !data?.anthropic_key) {
      throw new Error('No Anthropic API key found');
    } else if (provider === 'gemini' && !data?.gemini_key) {
      throw new Error('No Gemini API key found');
    }

    switch (provider) {
      case 'openai':
        let openaiConfig: any;

        // If model is set to 'auto', select the best model based on input
        let selectedModel = model;
        if (model === 'auto') {
          // Use latest and best models: GPT-4o for complex tasks, GPT-4o Mini for simple ones
          if (prompt.length > 500 || prompt.includes("complex") || prompt.includes("detailed") || file) {
            selectedModel = 'gpt-4o';
          } else {
            selectedModel = 'gpt-4o-mini';
          }
        }
        
        if (file && file.type.includes('image/')) {
          // For image files, we need to use the vision model and include the image
          const base64Image = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(file);
          });
          
          openaiConfig = {
            model: 'gpt-4-vision-preview', // Always use vision model for images
            messages: [
              {
                role: "system",
                content: `You are a helpful assistant that generates ${type} descriptions. Please provide a ${outputStyle} response.`
              },
              {
                role: "user",
                content: [
                  { type: "text", text: prompt },
                  { type: "image_url", image_url: { url: base64Image } }
                ]
              }
            ]
          };
        } else {
          // For text-only prompts
          openaiConfig = {
            model: selectedModel,
            messages: [
              {
                role: 'system',
                content: `You are a helpful assistant that generates ${type} descriptions. Please provide a ${outputStyle} response.`
              },
              {
                role: 'user',
                content: prompt
              }
            ]
          };
        }

        const response = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${data.llm_key}`
          },
          body: JSON.stringify(openaiConfig)
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error?.message || 'Failed to generate description');
        }

        const result = await response.json();
        return result.choices[0].message.content;

      case 'anthropic':
        if (!data.anthropic_key) throw new Error('No Anthropic API key found');
        
        const anthropicConfig = {
          model: model,
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 4000
        };
        
        const anthropicResponse = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': data.anthropic_key,
            'anthropic-version': '2023-06-01'
          },
          body: JSON.stringify(anthropicConfig)
        });
        
        if (!anthropicResponse.ok) {
          const error = await anthropicResponse.json();
          throw new Error(error.error?.message || 'Failed to generate description with Anthropic');
        }
        
        const anthropicResult = await anthropicResponse.json();
        return anthropicResult.content[0].text;
        
      case 'gemini':
        if (!data.gemini_key) throw new Error('No Gemini API key found');
        
        const geminiConfig = {
          contents: [
            {
              parts: [
                { text: `You are a helpful assistant that generates ${type} descriptions. Please provide a ${outputStyle} response.\n\n${prompt}` }
              ]
            }
          ],
          generationConfig: {
            temperature: 0.7,
            maxOutputTokens: 4000
          }
        };
        
        const geminiResponse = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${data.gemini_key}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(geminiConfig)
        });
        
        if (!geminiResponse.ok) {
          const error = await geminiResponse.json();
          throw new Error(error.error?.message || 'Failed to generate description with Gemini');
        }
        
        const geminiResult = await geminiResponse.json();
        return geminiResult.candidates[0].content.parts[0].text;

      default:
        throw new Error(`Unsupported provider: ${provider}`);
    }
  } catch (error) {
    console.error('Error generating task description:', error);
    throw error;
  }
}

export async function generateImage(prompt: string): Promise<string | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('llm_key')
      .eq('user_id', session.user.id)
      .single();

    if (error) throw error;
    if (!data?.llm_key) throw new Error('No OpenAI API key found');

    const response = await fetch('https://api.openai.com/v1/images/generations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${data.llm_key}`
      },
      body: JSON.stringify({
        model: 'dall-e-3',
        prompt: prompt,
        n: 1,
        size: '1024x1024'
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to generate image');
    }

    const result = await response.json();
    const imageUrl = result.data?.[0]?.url;
    return imageUrl || null;
  } catch (error) {
    console.error('Error generating image:', error);
    throw error;
  }
}